import * as React from "react";
import { Row, Col } from "react-bootstrap";
import "./Animation.scss";
import { gsap, Power4 } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useLayoutEffect, useRef, useState } from "react";
import SmileGroupAnimation from "./SmileGroupAnimation";
import PropTypes from "prop-types";

gsap.registerPlugin(ScrollTrigger);

// Green Box
function GreenBox({ children }) {
  return (
    <div id="green-box" className="box green m-0 p-0">
      {children}
    </div>
  );
}

// Green Box ScaleY animation
function GreenBoxAnimation(props) {
  return gsap.from(
    "#green-box",
    {
      scaleY: 0,
      duration: props.duration,
      delay: props.delay,
      ease: "slow(0.7, 0.1, true)",
      stagger: {
        amount: 5,
        each: 5,
      },
    },
    0
  );
}

// Smile Opacity Delayed Animation
function SmileAnimation(props) {
  return gsap.from(
    "#smile-" + props.index,
    {
      opacity: 0,
      duration: 20,
      delay: (props.index + 5) * 8,
      ease: Power4.easeIn,
      stagger: 10,
      // ease: "none",
    },
    0
  );
}

// Year Digit
function DigitEnterAnimation(props) {
  return gsap.fromTo(
    "#digit-" + props.index,
    {
      y: props.from,
    },
    {
      delay: props.delay,
      duration: props.duration,
      stagger: 10,
      ease: props.ease,
      y: props.to,
    },
    0
  );
}

// Year Unit/Dozens Digit
function DigitUnitEnterAnimation(props) {
  return gsap.to(
    "#digit-" + props.index,
    {
      delay: props.delay,
      ease: props.ease,
      duration: props.duration,
      stagger: 10,
      y: props.to,
    },
    0
  );
}

// Number Counter Animation
function CounterAnimation(props) {
  const items = document.querySelectorAll("#counter");
  return gsap.from(
    items,
    {
      textContent: 0,
      duration: props.duration,
      delay: props.delay,
      snap: { textContent: 5 },
      stagger: 10,
    },
    0
  );
}

const Animation = (props) => {
  const el = useRef();
  const easeDigit = "slow(0.7, 0.4, false)";
  const positionRelative = "relative";
  const positionFixed = "fixed";

  // Prop used to load or not the rest of the content in the page
  const [animationPosition, setAnimationPosition] = useState(positionRelative);

  // let smiles = calcSmilesCount();
  const smiles = 20;
  const scrollVelocityOffset = 4;

  gsap.config({
    nullTargetWarn: false,
    // trialWarn: false,
  });

  // This is the GSAP animation timeline with the ScrollTrigger plugin.
  // GreenBox, Smiles and Digits start all together at the beginning of the timeline.
  // DozensDigit start rolling when UnitDigit is rolling between 9 and 0.
  const tl = gsap.timeline({
    scrollTrigger: {
      trigger: ".hevelop-animation-wrapper",
      start: "top top",
      end: "+=" + window.innerHeight * scrollVelocityOffset,
      scrub: true,
      pin: true,
      anticipatePin: true,
      //markers: true, // useful on development
    },
    onStart() {
      // Prop used to load or not the rest of the content
      props.setAnimationCompleted(false);

      // Block the screen on the animation forcing user to scroll
      setAnimationPosition(positionFixed);
    },
    onReverseComplete() {
      props.setAnimationCompleted(true);
      setAnimationPosition(positionRelative);
    },
  });

  useLayoutEffect(() => {
    const yMove = -223;

    tl.add("scene1")

      // Green Box Bar Enter
      .add(GreenBoxAnimation({ duration: 220, delay: 55 }), "scene1")

      // Counter
      .add(CounterAnimation({ duration: 215, delay: 55 }), "scene1")

      // Year First and Second Digit Enter
      .add(
        DigitEnterAnimation({
          index: 1,
          from: 250,
          to: 0,
          delay: 60,
          duration: 20,
          ease: easeDigit,
        }),
        "scene1"
      )
      .add(
        DigitEnterAnimation({
          index: 2,
          from: 250,
          to: 0,
          delay: 70,
          duration: 20,
          ease: easeDigit,
        }),
        "scene1"
      )

      // Dozens Digit Enter
      .add(
        DigitEnterAnimation({
          index: 3,
          from: 250,
          to: 0,
          delay: 85,
          duration: 20,
          ease: easeDigit,
        }),
        "scene1"
      )

      // Units Digit Rolling
      .add(
        DigitEnterAnimation({
          index: 4,
          from: 250,
          to: 0,
          delay: 100,
          duration: 20,
          ease: easeDigit,
        }),
        "scene1"
      )
      .add(
        DigitUnitEnterAnimation({
          index: 4,
          to: yMove,
          delay: 130,
          duration: 20,
          ease: easeDigit,
          stagger: 2,
        }),
        "scene1"
      )
      .add(
        DigitUnitEnterAnimation({
          index: 4,
          to: yMove * 2,
          delay: 160,
          duration: 20,
          ease: easeDigit,
          stagger: 2,
        }),
        "scene1"
      )
      .add(
        DigitUnitEnterAnimation({
          index: 4,
          to: yMove * 3,
          delay: 190,
          duration: 20,
          ease: easeDigit,
          stagger: 2,
        }),
        "scene1"
      )
      .add(
        DigitUnitEnterAnimation({
          index: 4,
          to: yMove * 4,
          delay: 220,
          duration: 20,
          ease: easeDigit,
          stagger: 2,
        }),
        "scene1"
      )
      .add(
        DigitUnitEnterAnimation({
          index: 4,
          to: yMove * 5,
          delay: 245,
          duration: 20,
          ease: easeDigit,
          stagger: 2,
        }),
        "scene1"
      )

      // Dozens Digit Rolling
      .add(
        gsap.to(
          "#digit-" + 3,
          {
            delay: 240,
            duration: 20,
            stagger: 4,
            ease: easeDigit,
            y: yMove,
          },
          0
        ),
        "scene1"
      );

    // Manage all the Smiles animation
    for (var i = 0; i <= smiles; i++) {
      tl.add(SmileAnimation({ index: i + 1 }), "scene1");
    }

    // Fake tween animation used to add a pause at the end of timeline
    tl.add(
      gsap.to(
        {},
        {
          ease: 1,
          duration: 5,
          delay: 260,
          onComplete() {
            // Disable ScrollTrigger
            tl.scrollTrigger.disable(false);

            // Load the rest of the page
            props.setAnimationCompleted(true);

            // Enabled the rest of the page
            setAnimationPosition(positionRelative);
          },
        }
      ),
      "scene1"
    );
  }, []);

  return (
    <div className="hevelop-animation-wrapper">
      <div
        ref={el}
        className="hevelop-animation"
        style={{ position: animationPosition }}
      >
        <Row className="p-0 m-0 ">
          <Col className="col-lg-6 col-md-6 col-6 m-0 p-0 green-box-container">
            <Row className="col-lg-12 col-md-12 col-12 p-0 m-0">
              <GreenBox />
              <div className="digit-year-container d-flex align-items-start justify-content-center">
                <div className="digit-year" id="digit-1">
                  2
                </div>
                <div className="digit-year" id="digit-2">
                  0
                </div>
                <div className="digit-year" id="digit-3">
                  1<br />2
                </div>
                <div className="digit-year" id="digit-4">
                  5<br />
                  6<br />
                  7<br />
                  8<br />
                  9<br />
                  0<br />
                </div>
              </div>

              <div className="number-container m-0 p-0">
                <div className="text-end number-text" id="counter">
                  2500
                </div>
                <div className="text-start number-text">K+</div>
              </div>
              <div className="number-container m-0 p-0">
                <div className="text-center caption-text1">
                  {props.data.revenues}
                </div>
              </div>
              <div className="number-container2 m-0 p-0">
                <div className="text-center caption-text2">
                  {props.data.developers}
                </div>
              </div>
            </Row>
          </Col>
          <Col className="col-lg-6 m-0 p-0 text-end smile-container ">
            <SmileGroupAnimation />
          </Col>
        </Row>
      </div>
    </div>
  );
};

Animation.propTypes = {
  setAnimationCompleted: PropTypes.bool,
  data: PropTypes.object.isRequired,
};

GreenBox.propTypes = {
  children: PropTypes.string,
};

export default Animation;
